"use strict";

exports.__esModule = true;
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _clsx = _interopRequireDefault(require("clsx"));
var _reactPopper = require("react-popper");
var _useRandomId = _interopRequireDefault(require("../../hooks/useRandomId"));
var _useToggler2 = _interopRequireDefault(require("../../hooks/useToggler"));
var _useTrigger2 = _interopRequireDefault(require("../../hooks/useTrigger"));
var _useOnClickOutside = _interopRequireDefault(require("../../hooks/useOnClickOutside"));
var _excluded = ["isOpen", "trigger", "id", "position", "children", "className", "closeButton", "displayOnHover", "onClick", "width"];
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
// import Button from '../Button/Button';

/**
 * The Popover component provides a way to display additional information when an user hover another UI element.
 *
 * This component is an opinionated wrapper around <a href="https://github.com/popperjs/react-popper">ReactPopper</a>.
 */
var Popover = function Popover(_ref) {
  var controlledIsOpen = _ref.isOpen,
    initialTrigger = _ref.trigger,
    controlledId = _ref.id,
    position = _ref.position,
    children = _ref.children,
    className = _ref.className,
    closeButton = _ref.closeButton,
    displayOnHover = _ref.displayOnHover,
    onClick = _ref.onClick,
    width = _ref.width,
    attributes = _objectWithoutPropertiesLoose(_ref, _excluded);
  var popoverRef = _react.default.useRef(null);
  var id = _react.default.useRef((0, _useRandomId.default)(controlledId)).current;
  var _useToggler = (0, _useToggler2.default)(controlledIsOpen),
    isOpen = _useToggler.isOpen,
    setOpen = _useToggler.setOpen,
    setClose = _useToggler.setClose,
    setToggle = _useToggler.setToggle;
  (0, _useOnClickOutside.default)(popoverRef, setClose);
  var handleMouseEnter = function handleMouseEnter(event) {
    if (displayOnHover && !isOpen) {
      setToggle();
      if (trigger.props && trigger.props.onClick) {
        trigger.props.onClick(event);
      }
    }
  };
  var handleMouseLeave = function handleMouseLeave() {
    if (displayOnHover) {
      setClose();
    }
  };
  var handleTriggerClick = function handleTriggerClick(event) {
    if (!displayOnHover) {
      setToggle();
    }
  };
  var handleOnFocus = function handleOnFocus() {
    if (displayOnHover) {
      setOpen();
    }
  };
  var handleOnBlur = function handleOnBlur() {
    if (displayOnHover) {
      setClose();
    }
  };
  var _useTrigger = (0, _useTrigger2.default)(initialTrigger, {
      onClick: handleTriggerClick,
      onFocus: handleOnFocus,
      onBlur: handleOnBlur,
      className: 'uk-popover__trigger',
      'aria-controls': id,
      'aria-describedby': id
    }, 'button'),
    trigger = _useTrigger.trigger;
  return /*#__PURE__*/_react.default.createElement("div", _extends({
    ref: popoverRef,
    className: (0, _clsx.default)('uk-popover', isOpen && 'is-active', className),
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave
  }, attributes), /*#__PURE__*/_react.default.createElement(_reactPopper.Manager, null, /*#__PURE__*/_react.default.createElement(_reactPopper.Reference, null, function (_ref2) {
    var ref = _ref2.ref;
    return /*#__PURE__*/_react.default.createElement("div", {
      ref: ref
    }, trigger);
  }), /*#__PURE__*/_react.default.createElement(_reactPopper.Popper, {
    placement: position
  }, function (_ref3) {
    var ref = _ref3.ref,
      placement = _ref3.placement;
    return /*#__PURE__*/_react.default.createElement("div", {
      ref: ref,
      "data-placement": placement,
      role: "tooltip",
      className: "uk-popover__content",
      id: id,
      "aria-hidden": !isOpen,
      tabIndex: isOpen ? '0' : '-1',
      style: width ? {
        minWidth: width
      } : {}
    }, typeof children === 'function' ? children({
      isOpen: isOpen,
      setClose: setClose
    }) : children, closeButton && /*#__PURE__*/_react.default.cloneElement(closeButton, {
      'aria-controls': id,
      onClick: function onClick() {
        return setClose();
      }
    }));
  })));
};
Popover.propTypes = {
  /**
   * ID used to meet accessibility standards (WAI-ARIA Tooltip Pattern)
   * NB: the ID will be randomly generated if not set.
   */
  id: _propTypes.default.string,
  /**
   * Trigger of the Popover
   * If the trigger isn't a React element, it will be wrapped within a <span/>
   */
  trigger: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]).isRequired,
  /**
   * Position of the Popover relative to its trigger
   */
  position: _propTypes.default.oneOf(['top', 'bottom', 'left', 'right']),
  /**
   * Show or hide the popover (controlled)
   */
  isOpen: _propTypes.default.bool,
  /**
   * Behavior of the Popover
   */
  displayOnHover: _propTypes.default.bool,
  /**
   * Add a React element to close the Popover
   */
  closeButton: _propTypes.default.element,
  /**
   * Change width props (default : auto)
   */
  width: _propTypes.default.string
};
Popover.defaultProps = {
  position: 'top',
  displayOnHover: false
};
var _default = Popover;
exports.default = _default;